import classNames from "classnames";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ReactComponent as ArrowSVG } from "assets/images/common/arrow.svg";
import { ReactComponent as CalendarSVG } from "assets/images/common/calendar.svg";

import { VALIDATION_CLASS } from "utils/constants";

import styles from "./style.module.scss";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const VALUE_FORMAT = "yyyy-MM-dd";
const DISPLAY_FORMAT = "dd.MM.yyyy";
const CALENDAR_HEIGHT = 340;

type DropdownLayout = {
  top: number;
  left: number;
  width: number;
  position: "fixed" | "absolute";
};

type PropTypes = {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  error?: string | boolean;
  hint?: string;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
};

const parseValue = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = parse(value, VALUE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
};

const DatePicker: FC<PropTypes> = ({
  label,
  value,
  onChange,
  error,
  hint,
  placeholder,
  isRequired,
  isDisabled,
  minDate,
  maxDate,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);

  const selectedDate = useMemo(() => parseValue(value), [value]);
  const [viewMonth, setViewMonth] = useState<Date>(() => selectedDate ?? new Date());
  const [view, setView] = useState<"days" | "months">("days");
  const [monthViewYear, setMonthViewYear] = useState<number>(
    () => (selectedDate ?? new Date()).getFullYear(),
  );

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [layout, setLayout] = useState<DropdownLayout | null>(null);

  const displayValue = selectedDate ? format(selectedDate, DISPLAY_FORMAT) : "";

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const isDayDisabled = useCallback(
    (day: Date) => {
      if (minDate && isBefore(startOfDay(day), startOfDay(minDate))) return true;
      if (maxDate && isAfter(startOfDay(day), startOfDay(maxDate))) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const handleToggle = useCallback(() => {
    if (isDisabled) return;
    setViewMonth(selectedDate ?? new Date());
    setView("days");
    setOpen((prev) => !prev);
  }, [isDisabled, selectedDate]);

  const handleOpenMonthView = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setMonthViewYear(viewMonth.getFullYear());
      setView("months");
    },
    [viewMonth],
  );

  const handlePrevYear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMonthViewYear((y) => y - 1);
  }, []);

  const handleNextYear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMonthViewYear((y) => y + 1);
  }, []);

  const handleSelectMonth = useCallback(
    (monthIndex: number) => {
      setViewMonth(new Date(monthViewYear, monthIndex, 1));
      setView("days");
    },
    [monthViewYear],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if ((e.key === "Enter" || e.key === " ") && e.target === ref.current?.querySelector(`.${styles.field}`)) {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle],
  );

  const handleSelectDay = useCallback(
    (day: Date) => {
      if (isDayDisabled(day)) return;
      onChange(format(day, VALUE_FORMAT));
      setOpen(false);
    },
    [isDayDisabled, onChange],
  );

  const handlePrevMonth = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }, []);

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const dialogEl = ref.current.closest("dialog");
    const calHeight = calendarRef.current?.offsetHeight ?? CALENDAR_HEIGHT;
    const GAP = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < calHeight + GAP && rect.top > calHeight + GAP;
    const topViewport = openAbove ? rect.top - calHeight - GAP : rect.bottom + GAP;

    if (dialogEl) {
      const dRect = dialogEl.getBoundingClientRect();
      setPortalTarget(dialogEl);
      setLayout({
        top: topViewport - dRect.top,
        left: rect.left - dRect.left,
        width: rect.width,
        position: "absolute",
      });
    } else {
      setPortalTarget(document.body);
      setLayout({
        top: topViewport,
        left: rect.left,
        width: rect.width,
        position: "fixed",
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setLayout(null);
      setPortalTarget(null);
      return;
    }
    updatePosition();
    const raf = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(raf);
  }, [isOpen, view, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || calendarRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasValue = !!displayValue;
  const today = new Date();

  return (
    <div
      ref={ref}
      className={classNames(styles.datepicker, className, { [styles.disabled]: isDisabled })}
      onKeyDown={handleKeyDown}
    >
      <div
        className={classNames(styles.field, {
          [styles.filled]: hasValue || isOpen,
          [styles.open]: isOpen,
          [styles.fieldError]: !!error,
          [VALIDATION_CLASS]: !!error,
        })}
        onClick={handleToggle}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        tabIndex={isDisabled ? -1 : 0}
      >
        <span className={classNames(styles.value, { [styles.placeholder]: !displayValue })}>
          {displayValue || placeholder || ""}
        </span>

        {!!label && (
          <span className={classNames(styles.label, { [styles.required]: isRequired })}>
            {label}
          </span>
        )}

        <span className={styles.icon}>
          <CalendarSVG />
        </span>
      </div>

      {!!error && typeof error === "string" && <div className={styles.errorText}>{error}</div>}
      {!!hint && !error && <div className={styles.hint}>{hint}</div>}

      {isOpen &&
        layout &&
        portalTarget &&
        createPortal(
          <div
            ref={calendarRef}
            className={styles.calendar}
            style={{
              position: layout.position,
              top: layout.top,
              left: layout.left,
              width: layout.width,
            }}
          >
            {view === "days" ? (
              <>
                <div className={styles.calendarHeader}>
                  <button
                    type="button"
                    className={styles.navButton}
                    onClick={handlePrevMonth}
                    aria-label="Previous month"
                  >
                    <ArrowSVG />
                  </button>
                  <button
                    type="button"
                    className={styles.monthLabel}
                    onClick={handleOpenMonthView}
                    aria-label="Select month"
                  >
                    {format(viewMonth, "LLLL yyyy")}
                  </button>
                  <button
                    type="button"
                    className={classNames(styles.navButton, styles.navButtonNext)}
                    onClick={handleNextMonth}
                    aria-label="Next month"
                  >
                    <ArrowSVG />
                  </button>
                </div>

                <div className={styles.weekdays}>
                  {WEEKDAYS.map((day) => (
                    <span key={day} className={styles.weekday}>
                      {day}
                    </span>
                  ))}
                </div>

                <div className={styles.grid}>
                  {days.map((day) => {
                    const disabled = isDayDisabled(day);
                    const outside = !isSameMonth(day, viewMonth);
                    const selected = !!selectedDate && isSameDay(day, selectedDate);
                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        disabled={disabled}
                        className={classNames(styles.day, {
                          [styles.dayOutside]: outside,
                          [styles.daySelected]: selected,
                          [styles.dayToday]: !selected && isToday(day),
                          [styles.dayDisabled]: disabled,
                        })}
                        onClick={() => handleSelectDay(day)}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className={styles.calendarHeader}>
                  <button
                    type="button"
                    className={styles.navButton}
                    onClick={handlePrevYear}
                    aria-label="Previous year"
                  >
                    <ArrowSVG />
                  </button>
                  <span className={styles.yearLabel}>{monthViewYear}</span>
                  <button
                    type="button"
                    className={classNames(styles.navButton, styles.navButtonNext)}
                    onClick={handleNextYear}
                    aria-label="Next year"
                  >
                    <ArrowSVG />
                  </button>
                </div>

                <div className={styles.monthsGrid}>
                  {MONTHS.map((monthName, index) => {
                    const selectedMonth =
                      !!selectedDate &&
                      monthViewYear === selectedDate.getFullYear() &&
                      index === selectedDate.getMonth();
                    const currentMonth =
                      monthViewYear === today.getFullYear() && index === today.getMonth();
                    return (
                      <button
                        key={monthName}
                        type="button"
                        className={classNames(styles.month, {
                          [styles.monthSelected]: selectedMonth,
                          [styles.monthToday]: !selectedMonth && currentMonth,
                        })}
                        onClick={() => handleSelectMonth(index)}
                      >
                        {monthName}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>,
          portalTarget,
        )}
    </div>
  );
};

export default memo(DatePicker);
